import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@/shared/hooks/useLocation';
import { useToast } from '@/shared/ui/Toast';
import type { ReviewReplyFormality, ReviewReplyPatientAddressing } from '../api/reviewsApi';
import { reviewsApi } from '../api/reviewsApi';
import { reviewKeys } from '../queryKeys';

export function useReviewSettings() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();
  const toast = useToast();

  const query = useQuery({
    queryKey: reviewKeys.settings(locationId),
    queryFn: () => reviewsApi.getSettings(locationId),
    enabled: !!locationId,
  });

  const update = useMutation({
    mutationFn: (input: {
      signature?: string;
      patientAddressing?: ReviewReplyPatientAddressing;
      formality?: ReviewReplyFormality;
      voiceNotes?: string | null;
      autoDraftThreshold?: number;
      aeoEnabled?: boolean;
      captureReasonStepEnabled?: boolean;
    }) => reviewsApi.updateSettings({ locationId, ...input }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.settings(locationId) });
      toast.success('Review settings saved');
    },
    onError: (err: unknown) => toast.error((err as Error).message ?? 'Could not save settings'),
  });

  return { ...query, update };
}
